import { motion } from "framer-motion";
import { Beaker, FlaskConical, Cpu, Microscope, Network } from "lucide-react";

const tiles = [
  {
    n: "01",
    title: "Therapeutic Innovation",
    body: "Life-saving formulations for trauma and wound care.",
    icon: Beaker,
    span: "md:col-span-2 md:row-span-2",
    featured: true,
    image:
      "/assets/ta9m4lh8_Therapeutic Innovation.jpg",
    bullets: [
      "Hemostatic formulations",
      "Hard-to-heal wound systems",
      "Self-activating delivery",
    ],
    tag: "BLUDE · LIFEX CORE",
  },
  {
    n: "02",
    title: "Advanced R&D",
    body: "Preclinical and clinical research for real-world impact.",
    icon: FlaskConical,
    span: "md:col-span-2",
    image:
      "/assets/nkgjuwtw_Advanced R&D.jpg",
    tag: "TECHX · LAB",
  },
  {
    n: "03",
    title: "MedTech & Software",
    body: "Custom platforms for biotech, hospitals, pharma.",
    icon: Cpu,
    span: "md:col-span-1",
    image:
      "/assets/qpr05k0n_MedTech & Software.jpg",
    tag: "TECHX · STACK",
  },
  {
    n: "04",
    title: "Translational Support",
    body: "Bridging lab discoveries to patient-ready solutions.",
    icon: Microscope,
    span: "md:col-span-1",
    image:
      "/assets/w4vrjic4_Translational Support.jpg",
    tag: "TECHX · BRIDGE",
  },
  {
    n: "05",
    title: "Collaborative Science",
    body: "Partnering with academia, industry & government.",
    icon: Network,
    span: "md:col-span-2",
    image:
      "/assets/6ibkw8x9_Collobarative Science.jpg",
    tag: "TECHX · NETWORK",
  },
];

const MediaCard = ({ t, i }) => {
  const Icon = t.icon;
  const isFeatured = !!t.featured;
  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: i * 0.08 }}
      className={`card-tile p-0 group ${t.span || ""} relative overflow-hidden ${
        isFeatured ? "min-h-[480px]" : "min-h-[260px]"
      }`}
      data-testid={`focus-card-${t.n}`}
    >
      {/* Background image */}
      <img
        src={t.image}
        alt={t.title}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-[1.4s] opacity-55 group-hover:opacity-75"
      />
      {/* Layered overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/85 to-[#0A0A0C]/40" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_15%,rgba(230,0,0,0.32),transparent_55%)]" />
      <div className="absolute inset-0 noise" />

      {/* Top row */}
      <div className="relative z-10 flex items-start justify-between p-6 sm:p-7">
        <div
          className={`w-11 h-11 rounded-xl flex items-center justify-center backdrop-blur-md transition-all ${
            isFeatured
              ? "bg-[#E60000]/20 border border-[#E60000]/50"
              : "bg-black/40 border border-white/15 group-hover:border-[#E60000]/40 group-hover:bg-[#E60000]/15"
          }`}
        >
          <Icon
            className={`w-5 h-5 ${
              isFeatured ? "text-[#FF6A4D]" : "text-white/85 group-hover:text-[#FF6A4D]"
            } transition-colors`}
          />
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className="font-mono text-[11px] tracking-widest text-white/50">
            {t.n}
          </span>
          {isFeatured && (
            <span className="px-2.5 py-1 rounded-full bg-black/50 backdrop-blur border border-white/10 font-mono text-[10px] tracking-[0.25em] text-[#FF6A4D]">
              ● FEATURED
            </span>
          )}
        </div>
      </div>

      {/* Featured: middle bullets */}
      {isFeatured && t.bullets && (
        <div className="relative z-10 px-7 mt-2">
          <ul className="space-y-2 max-w-xs">
            {t.bullets.map((b) => (
              <li
                key={b}
                className="flex items-center gap-2.5 text-[13px] text-white/85"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF2400] shadow-[0_0_10px_rgba(255,36,0,0.8)]" />
                {b}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Bottom content */}
      <div
        className={`absolute z-10 inset-x-0 bottom-0 p-6 sm:p-7 ${
          isFeatured ? "" : ""
        }`}
      >
        <h3
          className={`font-display text-white tracking-tight leading-[1.05] ${
            isFeatured ? "text-3xl sm:text-4xl" : "text-xl sm:text-2xl"
          }`}
        >
          {t.title}
        </h3>
        <p
          className={`mt-2 text-white/65 leading-relaxed max-w-sm ${
            isFeatured ? "text-[14px] mt-3" : "text-[13px]"
          }`}
        >
          {t.body}
        </p>
        {t.tag && (
          <div className="mt-4 flex items-center gap-2.5 font-mono text-[10px] tracking-[0.3em] text-white/45">
            {isFeatured && <span className="dot-pulse" />}
            {t.tag}
          </div>
        )}
      </div>

      {/* Glow border on hover */}
      <div className="absolute inset-0 rounded-[20px] pointer-events-none ring-1 ring-inset ring-white/5 group-hover:ring-[#E60000]/40 transition-colors duration-500" />
    </motion.div>
  );
};

export const FocusAreas = () => {
  return (
    <section
      id="focus"
      data-testid="focus-section"
      className="relative section-pad bg-[#0A0A0C]"
    >
      <div className="absolute inset-x-0 top-0 divider-grad" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-2 h-2 rounded-full bg-[#E60000]" />
          <span className="eyebrow">FOCUS · 002</span>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 font-display text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter font-medium text-white"
          >
            Five frontlines
            <br />
            of critical{" "}
            <span className="italic font-light">intervention.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5 text-base sm:text-lg text-blude-textDim self-end text-justify"
          >
            We bridge critical gaps in trauma response, wound care, and rapid
            drug delivery through R&amp;D-backed medical formulations, devices,
            and AI-integrated software for hospitals, civilians, and frontline
            professionals.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-4 auto-rows-[minmax(220px,auto)] gap-4">
          {tiles.map((t, i) => (
            <MediaCard key={t.n} t={t} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

